import {FormType} from './../../enums/form-type';
import {
  FormAllData,
  FormParts,
  FormRadioElement,
  FormSelectOptGroup,
  FormSelectOption,
  FormStep,
} from './../../interfaces/forms/form-element';

import {Injectable} from '@angular/core';
import {
  FormControl,
  FormControlName,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {Observable, Subject, throwError} from 'rxjs';
import {catchError, distinctUntilChanged, map} from 'rxjs/operators';
import {FieldType} from '../../enums/field-type';
import {ApiUploadObjectFormatted, FormReturn} from '../../interfaces/api-object-formatted';
import {FormRadio, FormSelect} from '../../interfaces/forms/form-element';
import {FormElements} from '../../interfaces/forms/form-elements';

import {StepEventLike} from '../../interfaces/step-event-like';
import {ErrorHandlerService} from '../error-handler/error-handler.service';
import {FieldService} from '../field/field.service';
import {HttpService} from '../http/http.service';
import {formGroupValidator} from '../../validators/formgroup.validator';

@Injectable()
export class FormService {
  constructor(
    private httpService: HttpService,
    private errorService: ErrorHandlerService,
    private fieldService: FieldService,
  ) {}

  private formPartSubject: Subject<FormAllData[]> = new Subject();
  private formValueSubject: Subject<unknown> = new Subject();

  private formUploadSubject: Subject<ApiUploadObjectFormatted> = new Subject();
  private _completedSubject: Subject<any> = new Subject();

  private _formGroup: Subject<FormGroup> = new Subject();

  private _formStepSubject: Subject<StepEventLike> = new Subject();

  getFormType(data: unknown): FormType {
    const singleElToCheck = Array.isArray(data) ? data[0] : data;
    if (<FormStep>singleElToCheck.title && <FormStep>singleElToCheck.step) {
      return FormType.STEP;
    }
    if (typeof (<FormAllData>singleElToCheck.typeInterface) !== 'undefined') {
      return FormType.FORM;
    }
    return FormType.UNKNOWN;
  }

  addFormPart(list: FormAllData[]) {
    this.formPartSubject.next(list);
  }

  getFormPart(): Observable<FormAllData[]> {
    return this.formPartSubject.asObservable().pipe(distinctUntilChanged());
  }

  setFormValue(value: unknown) {
    this.formValueSubject.next(value);
  }

  getFormValue(): Observable<unknown> {
    return this.formValueSubject.asObservable().pipe(distinctUntilChanged());
  }

  generateFormGroup(
    formPart: FormParts<FormAllData>,
    required = false,
    stepName: string = 'unknownStep',
  ): FormGroup<FormElements> {
    const {form} = formPart;
    const formControls: FormElements = this.AddFormFields(form, stepName);
    const validators = [];
    if (formPart.showFormGroupErrors) {
      validators.push(formGroupValidator());
    }
    if (required) {
      validators.push(Validators.required);
    }

    return new FormGroup(formControls, validators);
  }

  generateStepFormGroup(
    stepData: FormAllData[],
    required: boolean,
    formGroup: FormGroup,
    stepName: string = 'unknownStep',
  ): FormGroup {
    const formGroupStep = formGroup.get(stepName) as FormGroup;
    stepData.forEach((el) => {
      const ctrl = formGroupStep.get(el.name ?? '');

      const updatedValues =
        this.fieldService.getParamsSync(el.name ?? el.id ?? '', stepName) ?? ({} as FormAllData);
      (<(keyof FormAllData)[]>Object.keys(updatedValues)).forEach((key) => {
        (<any>el[key]) = updatedValues[key];
      });
      this.fieldService.deleteParam(el.name ?? el.id ?? '', stepName);
      if (ctrl && el.hidden && el.name) {
        formGroupStep.removeControl(el.name);
      }
      if (el.typeInterface !== FieldType.TITLE && el.name && !el.hidden) {
        formGroupStep.setControl(el.name, this.AddFormField(el), {
          emitEvent: false,
        });
      }
    });

    // formGroup.get(stepName).updateValueAndValidity({onlySelf: true, emitEvent: true})
    return formGroup;
  }

  generateBaseStepsFormGroup(formData: FormStep[] = []): FormGroup<FormElements<unknown>> {
    const formControls: FormElements = formData.reduce((obj: FormElements<unknown>, el) => {
      /**/
      const fb = new FormGroup({});
      fb.disabled;
      obj[el.name] = fb as unknown as FormControl<unknown>;
      /*/
      const fb = this.generateFormGroup(el.step);
      fb.setValidators(Validators.required);
      obj[el.name] = fb;
      /**/
      return obj;
    }, {});
    return new FormGroup<FormElements>(formControls);
  }

  generateStepsFormGroup(
    currentStep: number,
    formData: FormStep[],
    formGroup: FormGroup,
  ): FormGroup {
    const currentStepData = formData[currentStep];
    formGroup = this.generateStepFormGroup(
      currentStepData.step,
      true,
      formGroup,
      currentStepData.name,
    );

    return formGroup;
  }

  AddFormFields(formData: FormAllData[], stepName?: string): FormElements {
    const formControls: FormElements = {};
    formData.forEach(async (el) => {
      if (this.fieldService.checkSubject(el.name ?? el.id ?? '', stepName)) {
        const updatedValues =
          this.fieldService.getParamsSync(el.name ?? el.id ?? '', stepName) ?? ({} as FormAllData);
        (<(keyof FormAllData)[]>Object.keys(updatedValues)).forEach((key) => {
          (<any>el[key]) = updatedValues[key];
        });
      }
      if (el.typeInterface !== FieldType.TITLE && el.name && !el.hidden) {
        const fc = this.AddFormField(el);

        formControls[el.name] = fc;
      }
    });
    return formControls;
  }
  AddFormField(el: FormAllData): FormControl {
    const value = this.getDefaultValue(el);

    const fc = new FormControl(value ?? null, {
      validators: (el.error as ValidatorFn[]) ?? [],
    });

    if ((<any>el).disabled) {
      fc.disable({emitEvent: false});
    }

    return fc;
  }

  getDefaultValue(el: FormAllData) {
    const typeInterface: FieldType = el.typeInterface;

    switch (typeInterface) {
      case FieldType.RADIO:
      case FieldType.TOGGLE_BUTTON: {
        const radioElement: FormRadio = el as FormRadio;
        const radioList: FormRadioElement[] = radioElement.list;

        const checked: unknown =
          radioList.find((elem) => {
            return elem.selected;
          })?.value ?? null;
        return checked;
        break;
      }
      case FieldType.SELECT: {
        const select: FormSelect = el as FormSelect;
        const {selectSingleResult, list, value}: FormSelect<FormSelectOption | FormSelectOptGroup> =
          select;
        if (value) {
          return (
            (<FormSelectOption[]>list).find((el: FormSelectOption) => {
              return el.value === value || el.selected;
            })?.value ?? null
          );
        }
        if (selectSingleResult || typeof selectSingleResult === 'undefined') {
          if (list.length === 1) {
            if ((list[0] as FormSelectOption).value) {
              return (list[0] as FormSelectOption).value;
            }
            const formOpt = list[0] as FormSelectOptGroup;
            const hasOpt = typeof formOpt.list !== undefined;
            if (hasOpt) {
              if (formOpt.list?.length === 1) {
                return formOpt.list[0].value;
              }
            }
          }
        }

        break;
      }
    }
    return el.resetValue ? null : (el.value ?? null);
  }

  setFormsDefault(list: {key: string; value: string}[], form: FormGroup): void {
    list.forEach((el) => {
      form.get(el.key)?.setValue(el.value);
    });
  }

  getErrorList(form: FormGroup): {field: string; errors: string[]}[] {
    const fields = Object.keys(form.controls);

    return fields.reduce((arr: {field: string; errors: string[]}[], field) => {
      const errors = form.get(field)?.errors;
      if (errors) {
        arr.push({field: field, errors: Object.keys(errors)});
      }
      return arr;
    }, []);

    // fields.forEach((field) => {
    //   const errorObjArr = Object.keys(form.get(field)?.errors || []);
    //   errorObjArr.forEach((errType) => {
    //     if (!errorList[field]) {
    //       errorList[field] = [];
    //     }
    //     errorList[field].push(errType);
    //   });
    // });
    // return errorList;
  }

  setUploadFile(form: ApiUploadObjectFormatted): void {
    this.formUploadSubject.next(form);
  }

  setUploadErrorFile(form: ApiUploadObjectFormatted): void {
    this.formUploadSubject.error(form);
  }

  getUploadFile(): Observable<ApiUploadObjectFormatted> {
    return this.formUploadSubject.asObservable();
  }

  setForm(data: FormGroup): Observable<FormReturn> {
    return new Observable((ret) => {
      ret.next(this.createFormObject(data.getRawValue()));
    });
  }

  createFormObject(form: any, token?: string): FormReturn {
    const defForm = {
      captcha: token || null,
      data: {},
    };
    if (form) {
      defForm.data = {...defForm.data, ...form};
    }
    return defForm;
  }

  sendHttpForm(
    {url, formData, options}: {url: string; formData: any; options?: any},
    showModal: boolean = true,
    resetForm: boolean = false,
  ): Observable<any> {
    return this.httpService.post(url, formData, options).pipe(
      catchError((err) => {
        this.errorService.showError(err);
        return throwError(err);
      }),
      map((el) => {
        this._completedSubject.next({showModal, resetForm});
        return el;
      }),
    );
  }

  resetAllModal() {}

  getCompletedForm() {
    return this._completedSubject.asObservable();
  }

  saveFormGroup(formGroup: FormGroup) {
    this._formGroup.next(formGroup);
  }
  getFormGroup(): Observable<FormGroup> {
    return this._formGroup.asObservable();
  }

  isFormStepInteractObserved(): boolean {
    return this._formStepSubject.observers.length > 0;
  }
  setFormStepInteract(formStep: StepEventLike) {
    this._formStepSubject.next(formStep);
  }

  getFormStepInteract(): Observable<StepEventLike> {
    return this._formStepSubject.asObservable();
  }
}
